import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/dist/query/react';
import { baseUrl } from '../authService/authService';
import { DataType } from '../../modules/tables/interfaces/interfaces';
import { IMaxDataItem } from '../../modules/tables/TablesSlice';

export const projectsApiService = createApi({
  reducerPath: 'projectsApiService',
  baseQuery: fetchBaseQuery({
    baseUrl: baseUrl,
    credentials: 'include',
  }),
  tagTypes: ['TableAllProjects'],
  endpoints: (build) => ({
    getProjects: build.mutation<DataType[], { skip: number; limit: number }>({
      query: ({ skip, limit }) => ({
        url: '/cda/read_projects',
        params: {
          skip,
          limit,
        },
      }),
      // providesTags: () => ['TableAllProjects'],
    }),
    getProject: build.mutation<DataType, { id: number }>({
      query: ({ id }) => ({
        url: '/cda/get_project',
        params: {
          project_id: id,
        },
      }),
    }),
    deleteProject: build.mutation<DataType, { id: number }>({
      query: ({ id }) => ({
        url: '/cda/delete_project',
        method: 'DELETE',
        params: {
          project_id: id,
        },
      }),
    }),
    getProjectXPathStructure: build.mutation<IMaxDataItem[], { id: number }>({
      query: ({ id }) => ({
        url: '/cda/get_xpath_by_project_id',
        params: {
          project_id: id,
        },
      }),
    }),
    saveProjectXPathStructure: build.mutation<IMaxDataItem[], { id: number; data: IMaxDataItem[] }>(
      {
        query: ({ data, id }) => ({
          url: '/cda/update_xpath',
          method: 'POST',
          body: data,
          params: { project_id: id },
        }),
      },
    ),
    //CDA
    parseXmlDoc: build.mutation<{ data: string; filename: string }, unknown>({
      query: (data) => ({
        url: '/files/upload_file/',
        method: 'POST',
        body: data,
      }),
    }),
    parseXlsxDoc: build.mutation<IMaxDataItem[], unknown>({
      query: (data) => ({
        url: '/files/upload_file/',
        method: 'POST',
        body: data,
      }),
    }),
    //targetShowCaseTable
    parseXlsxDocForTargetShowcase: build.mutation<{ data: string; filename: string }, unknown>({
      query: (data) => ({
        url: '/files/upload_file_for_showcase/',
        method: 'POST',
        body: data,
      }),
    }),
    createXlsxDocForTargetShowcase: build.mutation<{ data: string; filename: string }, unknown>({
      query: ({ body, projectName }) => ({
        url: '/files/create_xlsx_file_for_showcase',
        method: 'POST',
        params: { project_name: projectName },
        body: body,
      }),
    }),
    addNewProject: build.mutation<object, unknown>({
      query: (data) => ({
        url: '/cda/add_project',
        method: 'POST',
        body: data,
      }),
    }),
    editNewProject: build.mutation<object, unknown>({
      query: ({ data, id }) => ({
        url: '/cda/update_projects',
        method: 'PATCH',
        body: { ...data },
        params: { project_id: id },
      }),
    }),
    getDocsTypes: build.query<
      Array<{ id: number; code: string; display: string }>,
      { skip: number; limit: number }
    >({
      query: ({ skip, limit }) => ({
        url: '/cda/read_docs',
        params: {
          skip,
          limit,
        },
      }),
      providesTags: () => ['TableAllProjects'],
    }),

    getXPathFileInfo: build.mutation<object, unknown>({
      query: ({ body, projectName }) => ({
        url: '/files/create_xlsx_file',
        method: 'POST',
        params: { project_name: projectName },
        body: body,
      }),
    }),

    getXPathFileXlsx: build.mutation<object, unknown>({
      query: ({ filename }) => ({
        url: `/files/download/${filename}`,
        method: 'GET',
        responseHandler: async (response) => {
          const file = window.URL.createObjectURL(await response.blob());
          const link = document.createElement('a');
          link.href = file;
          link.setAttribute('download', filename);
          document.body.appendChild(link);
          link.click();
          link.parentNode.removeChild(link);
        },
      }),
    }),
    getCheckingCorrectnessXPath: build.mutation<Array<string>, unknown>({
      query: ({ file_name, xpath_string }) => ({
        url: '/cda/pars/pars_xpath',
        method: 'POST',
        params: { file_name, xpath_string },
      }),
    }),

    getTargetShowcaseData: build.mutation<object, { id: number }>({
      query: ({ id }) => ({
        url: '/cda/get_mart_field_transform',
        params: {
          project_id: id,
        },
      }),
      // providesTags: () => ['TableAllProjects'],
    }),
    getDescriptionTargetShowcase: build.query<Array<unknown>, { skip: number; limit: number }>({
      query: ({ skip, limit }) => ({
        url: '/cda/read_transformation_function',
        params: {
          skip,
          limit,
        },
      }),
      providesTags: () => ['TableAllProjects'],
    }),
    getAttributeTypeOptions: build.query<object, { skip: number; limit: number }>({
      query: ({ skip, limit }) => ({
        url: '/cda/read_field_types',
        params: {
          skip,
          limit,
        },
      }),
      providesTags: () => ['TableAllProjects'],
    }),
    editXPathTargetShowCase: build.mutation<object, { id: number; body: unknown }>({
      query: ({ id, body }) => ({
        url: '/cda/edit_mart_column_n_mart_field_transform',
        method: 'PATCH',
        params: { project_id: id },
        body: body,
      }),
    }),
    checkAllXPath: build.mutation<
      object,
      Array<{ code: string; xpath: string; file_name: string }>
    >({
      query: (body) => ({
        url: '/cda/pars/pars_xpath_all',
        method: 'POST',
        body: body,
      }),
    }),
    sendEmail: build.mutation<object, unknown>({
      query: ({ data, id }) => ({
        url: '/cda/send_email',
        method: 'POST',
        body: { ...data },
        params: { project_id: id },
      }),
    }),
  }),
});
